import styled from '@emotion/styled';
import { Trans } from 'react-i18next';
import { tokens, Text, Link, Icon, Avatar } from '@hotjar/hotsauce';

import { timeAgo } from '@hotjar/webapp/lib/time';
import { SITE } from '@hotjar/webapp/constants/routes';
import { getRoute } from '@hotjar/webapp/utils/routing';
import { useTrack } from '@hotjar/webapp/hooks/useTrack';
import type { ActivityFeedEvent } from '@hotjar/webapp/domains/activity-feed/types';
import { ACTIVITY_FEED_CONTENT_TYPE } from '@hotjar/webapp/domains/activity-feed/types';
import { useCurrentSite } from '@hotjar/site';

const Wrapper = styled.div`
  display: flex;
  align-items: center;
`;

const Content = styled.div`
  flex: 1 1 auto;
  word-break: break-word;

  a {
    text-decoration: underline;
  }
`;

const TrackingCodeIcon = styled(Icon)`
  flex: 0 0 auto;
  margin-right: ${tokens.spacer.x2};
`;

const UserAvatar = styled(Avatar)`
  flex: 0 0 auto;
  margin-right: ${tokens.spacer.x2};
`;

const FeedEventData = styled.div`
  display: flex;
  flex-direction: column;
`;

const Metadata = styled(Text)`
  display: block;
`;

const Module = styled.span`
  text-transform: capitalize;
`;

const Quote = styled(Text)`
  border-left: 2px solid ${tokens.color.ui_border_light};
  padding-left: ${tokens.spacer.x1};
  margin: ${tokens.spacer.x0} 0;
  color ${tokens.color.text_light};

  &:before,
  &:after {
    content: '"';
  }
`;

const Bold = styled(Text)`
  font-weight: 600;
`;

const Mention = styled.b`
  font-weight: 600;
`;

const TRACKING_UI_POSITION = 'Dashboard Activity Feed Card';

export function TrackingCodeFeedItem({
  trackingUIPosition,
}: {
  trackingUIPosition?: string;
}): JSX.Element | null {
  const { data: currentSite } = useCurrentSite();
  const siteUrl = currentSite?.url || currentSite?.name || 'your site';
  const { click: trackClick } = useTrack();

  return currentSite ? (
    <Wrapper>
      <TrackingCodeIcon icon="check" size="l" color="text_success" />
      <Content>
        <div>
          <Trans
            i18nKey="activation:dashboard.activity-feed-card.tracking-code-installed"
            values={{ siteUrl }}
          >
            You successfully installed your Hotjar script on {siteUrl}. If you need to access your
            installation code again, you can do so from{' '}
            <Link
              kind="inline"
              href={getRoute(SITE.SETTINGS.path, { siteId: currentSite.id })}
              onClick={() =>
                trackClick('Feed Item Link', trackingUIPosition || TRACKING_UI_POSITION, {})
              }
            >
              site management
            </Link>
            .
          </Trans>
        </div>
      </Content>
    </Wrapper>
  ) : null;
}

export function ActivityFeedItem({
  action,
  module,
  description,
  created,
  linkText,
  linkUrl,
  username = '',
  pictureUrl,
  content,
  trackingUIPosition,
}: ActivityFeedEvent): JSX.Element {
  const { click: trackClick } = useTrack();

  return (
    <Wrapper>
      {pictureUrl ? <UserAvatar size="l" src={pictureUrl} userName={username} /> : null}
      <Content>
        <FeedEventData>
          <div>
            <Bold textType="caption" as="span">
              {username}
            </Bold>
            <Text textType="caption" as="span">
              {description ? ` ${action} ${description}` : ` ${action}`}
            </Text>
            {linkUrl ? (
              <>
                {' '}
                <Link
                  kind="inline"
                  href={linkUrl}
                  isExternal={linkText === 'Recording'}
                  onClick={() =>
                    trackClick('Feed Item Link', trackingUIPosition || TRACKING_UI_POSITION, {})
                  }
                >
                  {linkText || linkUrl}
                </Link>
              </>
            ) : null}
            .
          </div>
          {content ? (
            <Quote textType="caption" textColor="text_light">
              {content.map(({ uuid, type, value }) =>
                type === ACTIVITY_FEED_CONTENT_TYPE.MENTION ? (
                  <Mention key={uuid}>{value}</Mention>
                ) : (
                  value
                ),
              )}
            </Quote>
          ) : null}
        </FeedEventData>
        <Metadata textType="caption" textColor="text_disabled" as="span">
          <Module>{module}</Module>
          {` • ${timeAgo(created)}`}
        </Metadata>
      </Content>
    </Wrapper>
  );
}
